/**
 * Canonical status color palette — used in the Change Status dropdown (StatusMenu)
 * and anywhere else that renders status colors outside of the main jobs table.
 *
 * Values are chosen to match the MUI chip colors used by the jobs table's
 * StatusChip so the dropdown dots look identical to the table badges.
 *
 * MUI filled chip equivalents:
 *   error   → #d32f2f (red)
 *   info    → #0288d1 (blue)
 *   success → #388e3c (green)
 *   warning → #f57c00 (orange/amber)
 *   default → #616161 (grey)
 */
export const STATUS_COLORS: Record<string, string> = {
  // ── Aerial job statuses ──
  'Delivered':                   '#f57c00',  // warning  — amber/orange
  'As-Built':                    '#388e3c',  // success  — green
  'Invoiced':                    '#388e3c',  // success  — green
  'Approved for Construction':   '#388e3c',  // success  — green
  'Pending Photo Annotation':    '#0288d1',  // info     — blue
  'Lead QC':                       '#0d9488',  // teal     — QC review gate (pre-PE)
  'EMR Review':                    '#be185d',  // rose     — EMR review gate (pre-PE)
  'Pending LLD Financial Approval':'#7c3aed',  // violet   — LLD financial-approval gate (pre-PE)
  'Pending EMR':                 '#f57c00',  // warning  — amber/orange
  'Sent to PE':                  '#9c27b0',  // purple   — custom (matches table)
  'Pending Field Collection':    '#d32f2f',  // error    — red
  'Hold':                        '#d32f2f',  // error    — red
  'Junk':                        '#d32f2f',  // error    — red
  'Archived':                    '#616161',  // default  — grey
  // ── Permit statuses ──
  'Not Started':                 '#64748b',  // slate grey
  'In Progress':                 '#0288d1',  // sky blue
  'Pending Approval':            '#9c27b0',  // purple
  'Approved':                    '#388e3c',  // green
  'Closed':                      '#6366f1',  // indigo
};

export function getStatusColor(status: string): string {
  if (!status) return '#616161';
  // Exact match first
  if (STATUS_COLORS[status]) return STATUS_COLORS[status];
  // Fuzzy fallbacks
  const l = status.toLowerCase();
  if (l.includes('lead qc') || l.includes('lead-qc'))    return STATUS_COLORS['Lead QC'];
  if (l.includes('emr review') || l.includes('emr-review')) return STATUS_COLORS['EMR Review'];
  if (l.includes('lld financial') || l.includes('financial approval')) return STATUS_COLORS['Pending LLD Financial Approval'];
  if (l.includes('delivered'))                           return STATUS_COLORS['Delivered'];
  if (l.includes('as-built') || l.includes('as built')) return STATUS_COLORS['As-Built'];
  if (l.includes('invoiced'))                            return STATUS_COLORS['Invoiced'];
  if (l.includes('approved'))                            return STATUS_COLORS['Approved for Construction'];
  if (l.includes('pending photo'))                       return STATUS_COLORS['Pending Photo Annotation'];
  if (l.includes('pending emr'))                         return STATUS_COLORS['Pending EMR'];
  if (l.includes('sent to pe'))                          return STATUS_COLORS['Sent to PE'];
  if (l.includes('pending field'))                       return STATUS_COLORS['Pending Field Collection'];
  if (l.includes('hold') || l.includes('junk'))          return STATUS_COLORS['Hold'];
  if (l.includes('archive'))                             return STATUS_COLORS['Archived'];
  if (l.includes('not started'))                         return STATUS_COLORS['Not Started'];
  if (l.includes('in progress'))                         return STATUS_COLORS['In Progress'];
  if (l.includes('pending approval'))                    return STATUS_COLORS['Pending Approval'];
  if (l.includes('closed'))                              return STATUS_COLORS['Closed'];
  return '#616161';
}
